import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { 
  Check, 
  Star, 
  Shield, 
  Zap, 
  Users, 
  Crown,
  ArrowRight,
  Sparkles
} from "lucide-react";
import { useTrialManagement, PersonalizedOffer } from "@/hooks/useTrialManagement";

interface ConversionModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  offer?: PersonalizedOffer;
}

export const ConversionModal: React.FC<ConversionModalProps> = ({
  isOpen,
  onOpenChange,
  offer
}) => {
  const [selectedPlan, setSelectedPlan] = useState<'starter' | 'professional' | 'enterprise'>('professional');
  const { trackConversionEvent } = useTrialManagement();

  const plans = {
    starter: {
      name: 'Starter',
      price: 29,
      originalPrice: 39,
      features: [
        'Up to 100 bills/month',
        'Basic AI extraction',
        'Email support',
        'Standard integrations',
        'Mobile app access'
      ],
      icon: Zap,
      popular: false
    },
    professional: {
      name: 'Professional',
      price: 79,
      originalPrice: 99,
      features: [
        'Up to 1,000 bills/month',
        'Advanced AI extraction',
        'Priority support',
        'Advanced integrations',
        'API access',
        'Custom fields',
        'Team collaboration'
      ],
      icon: Crown,
      popular: true
    },
    enterprise: {
      name: 'Enterprise',
      price: 199,
      originalPrice: 249,
      features: [
        'Unlimited bills',
        'Enterprise AI models',
        'Dedicated support',
        'Custom integrations',
        'SSO & advanced security',
        'White-label options',
        'Custom onboarding',
        'SLA guarantee'
      ],
      icon: Shield,
      popular: false
    }
  };

  const handleUpgrade = (planType: keyof typeof plans) => {
    trackConversionEvent.mutate({
      event_type: 'upgrade_selected',
      event_data: { 
        plan: planType,
        source: 'conversion_modal',
        offer_type: offer?.offer_type
      }
    });

    // Here you would normally redirect to payment processing
    // For now, we'll just close the modal and track completion
    setTimeout(() => {
      trackConversionEvent.mutate({
        event_type: 'conversion_complete',
        event_data: { 
          plan: planType,
          final_price: plans[planType].price
        }
      });
      onOpenChange(false);
    }, 1000);
  };

  const getDiscountedPrice = (originalPrice: number) => {
    if (offer?.discount_percentage) {
      return Math.round(originalPrice * (1 - offer.discount_percentage / 100));
    }
    return originalPrice;
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader className="text-center">
          <DialogTitle className="text-2xl font-bold flex items-center justify-center gap-2">
            <Sparkles className="h-6 w-6 text-primary" />
            Upgrade Your Account
          </DialogTitle>
          <DialogDescription className="text-lg">
            {offer?.urgency_message || 'Choose the perfect plan for your needs'}
          </DialogDescription>
          
          {offer?.discount_percentage && (
            <Badge className="mx-auto bg-green-500 text-white text-sm px-3 py-1">
              Limited Time: {offer.discount_percentage}% OFF
            </Badge>
          )}
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
          {Object.entries(plans).map(([key, plan]) => {
            const planKey = key as keyof typeof plans;
            const Icon = plan.icon;
            const discountedPrice = getDiscountedPrice(plan.originalPrice);
            const isSelected = selectedPlan === planKey;

            return (
              <Card 
                key={key}
                className={`relative cursor-pointer transition-all ${
                  isSelected 
                    ? 'ring-2 ring-primary border-primary bg-primary/5' 
                    : 'hover:border-primary/50'
                } ${plan.popular ? 'border-primary' : ''}`}
                onClick={() => setSelectedPlan(planKey)}
              >
                {plan.popular && (
                  <Badge className="absolute -top-2 left-1/2 transform -translate-x-1/2 bg-primary text-white">
                    Most Popular
                  </Badge>
                )}
                
                <CardHeader className="text-center pb-2">
                  <div className="flex justify-center mb-2">
                    <Icon className="h-8 w-8 text-primary" />
                  </div>
                  <CardTitle className="text-xl">{plan.name}</CardTitle>
                  <div className="space-y-1">
                    <div className="flex items-center justify-center gap-2">
                      <span className="text-3xl font-bold text-primary">
                        ${discountedPrice}
                      </span>
                      <span className="text-sm text-muted-foreground">/month</span>
                    </div>
                    {offer?.discount_percentage && (
                      <div className="text-sm text-muted-foreground line-through">
                        Was ${plan.originalPrice}/month
                      </div>
                    )}
                  </div>
                </CardHeader>
                
                <CardContent>
                  <ul className="space-y-2">
                    {plan.features.map((feature, index) => (
                      <li key={index} className="flex items-center gap-2 text-sm">
                        <Check className="h-4 w-4 text-green-500 flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  {offer?.bonus_features && isSelected && (
                    <>
                      <Separator className="my-4" />
                      <div>
                        <p className="font-medium text-sm text-primary mb-2">
                          Bonus Features Included:
                        </p>
                        <ul className="space-y-1">
                          {offer.bonus_features.map((feature, index) => (
                            <li key={index} className="flex items-center gap-2 text-sm">
                              <Star className="h-4 w-4 text-yellow-500 flex-shrink-0" />
                              <span>{feature}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>

        <div className="mt-6 p-4 rounded-lg bg-accent/50 border border-accent">
          <div className="flex items-center gap-2 mb-2">
            <Shield className="h-5 w-5 text-primary" />
            <span className="font-medium">Risk-Free Guarantee</span>
          </div>
          <p className="text-sm text-muted-foreground">
            30-day money-back guarantee. Cancel anytime during your first month for a full refund.
          </p>
        </div>

        <DialogFooter className="flex flex-col sm:flex-row gap-3">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="order-2 sm:order-1"
          >
            Maybe Later
          </Button>
          <Button
            onClick={() => handleUpgrade(selectedPlan)}
            disabled={trackConversionEvent.isPending}
            className="order-1 sm:order-2 bg-primary hover:bg-primary/90"
          >
            {trackConversionEvent.isPending ? (
              'Processing...'
            ) : (
              <>
                {offer?.cta_text || 'Upgrade Now'}
                <ArrowRight className="ml-2 h-4 w-4" />
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};